import { useState } from "react";
import { PetSpeciesValues, type PetOrigin, type PetSpecies } from "@/shared/contracts";
import { env } from "@/shared/config/env";
import demoCatPortrait from "./assets/demo-cat-portrait-v1.webp";
import { sizedPhotoSrc, usePhotoSizes } from "./photoSizes";

/**
 * 全站头像取图的唯一规则（用户 2026-09-24：头像永远是 TA 自己的样子，不用名字首字）：
 * 有照片就用照片；演示（fixture）模式没有照片时用内部测试小灰猫（用户授权的演示对象，见 assets/demo-cat-portrait-provenance.md）；
 * live 没有照片时返回 null，显示中性爪印——不借演示猫冒充真实宠物。
 */
export function petPortraitUrl(photoUrl: string | null | undefined): string | null {
  if (photoUrl) return photoUrl;
  return env.dataMode === "fixture" ? demoCatPortrait : null;
}

/**
 * 已经有插画的物种（手写，不按契约的取值推）：UI-ASSET-002 v1 实际交付的 7 张（public/ui-assets/UI-ASSET-002/v1/ 下逐张核过），
 * 加 ASSET-UI-029 v1 的豚鼠、龙猫、刺猬、雪貂 4 张（SPECIES-ART-01；r7k 画，I 10-09 审过原件；原字节、原名和指纹见 public/ui-assets/ASSET-UI-029/v1/SOURCE.md）。
 * 契约的 11 种现在都有插画。表外的值（比如后端以后加了新物种、这里还没有图）照旧用 other 那一张，不拼一个不存在的地址（UI-BASE-01b，W-24）。
 * 再有新插画交付：把物种加进这张表；图不在 UI-ASSET-002 v1 下的，地址照下面 SPECIES_ART_029 的样子写进一张表。
 */
export const SPECIES_WITH_ART: ReadonlySet<string> = new Set(["bird", "cat", "chinchilla", "dog", "ferret", "guinea_pig", "hamster", "hedgehog", "other", "parrot", "rabbit"]);

/** ASSET-UI-029 v1 那 4 张的地址（文件名是原交付名去掉单号前缀，原名见 SOURCE.md）。其余物种的图都在 UI-ASSET-002 v1 下，叫 species-<物种>.webp。 */
const SPECIES_ART_029: Partial<Record<PetSpecies, string>> = {
  guinea_pig: "/ui-assets/ASSET-UI-029/v1/guinea-pig@192x192.webp",
  chinchilla: "/ui-assets/ASSET-UI-029/v1/chinchilla@192x192.webp",
  hedgehog: "/ui-assets/ASSET-UI-029/v1/hedgehog@192x192.webp",
  ferret: "/ui-assets/ASSET-UI-029/v1/ferret@192x192.webp",
};

/** 物种插画的地址：选物种、居民页、领养来的原创居民没照片时的头像、访客星球头图、证件照位、小游戏的物种替身，都是这一张；表里没有的值用 other。 */
export function speciesArtUrl(species: PetSpecies): string {
  const known: PetSpecies = SPECIES_WITH_ART.has(species) ? species : "other";
  return SPECIES_ART_029[known] ?? `/ui-assets/UI-ASSET-002/v1/species-${known}.webp`;
}

const SPECIES_NAMES: Record<PetSpecies, string> = { cat: "猫", dog: "狗", rabbit: "兔子", hamster: "仓鼠", bird: "小鸟", parrot: "鹦鹉", guinea_pig: "豚鼠", chinchilla: "龙猫", hedgehog: "刺猬", ferret: "雪貂", other: "小动物" };

/** 物种的中文叫法（插画的读屏、居民页的“小鸟 · 3 岁”等共用这一张表）。 */
export function speciesName(species: PetSpecies): string {
  return SPECIES_NAMES[species] ?? "小动物";
}

/**
 * 没有照片时用不用物种插画（2026-09-25 巡检 R10-03/04 对照 R10-07）：同一只秋秋，居民页、领养卡是小鸟插画，
 * 入住后到家卡片、地图标记却是爪印，刚领养的人认不出是自己选的那只。
 * 只有来历是原创居民（adopted_original）时才用：返回插画的物种，就是居民页 ResidentPortrait 用的那一张。
 * 自己的宠物（own_pet）、按真实档案领养的（adopted_real_archive）、来历不知道的都返回 null（爪印）——通用插画不冒充用户自己宠物的样子。
 * 物种认不出（不在契约的取值里）也是 null：不拿一张不存在的图去凑。PetAvatar 与 features/pets 的 PetPortrait 共用这一条。
 */
export function portraitFallback(origin: PetOrigin | null | undefined, species: string | null | undefined): PetSpecies | null {
  if (origin !== "adopted_original") return null;
  return PetSpeciesValues.find((value) => value === species) ?? null;
}

/** 物种插画的读屏说法：说清这是插画、不是 TA 本人的照片（居民页 ResidentPortrait、PetAvatar、PetPortrait、证件照位都用这一句）。 */
export function speciesArtLabel(species: PetSpecies, name: string): string {
  return `${speciesName(species)}的插画，不是${name}本人的照片`;
}

/**
 * 头像退回链的一级（2026-09-27 D4 第二批）：TA 的照片 → 演示小灰猫（只在演示模式）→ 物种插画（只给领养来的原创居民）或爪印。
 * 链的第一级就是原来 petPortraitUrl + portraitFallback 定下的那一个，所以图不出错时画法和以前完全一样；只有图打不开时才往下退。
 */
export type AvatarStep = { kind: "photo"; src: string } | { kind: "demo"; src: string } | { kind: "species"; species: PetSpecies } | { kind: "paw" };

/** 浏览器解析网址时会删掉制表符和换行、把反斜杠当斜杠（`/\t/别的站`、`/\别的站` 实际去了别的站）：带这些字符的一律不收。写法同公告链接的站内路径判断。 */
const UNSAFE_URL_CHARS = /[\s\u0000-\u001f\u007f\\]/;
/** 只用来判断“解析后还在不在本站”的假站点，从不发请求。 */
const PROBE_ORIGIN = "https://petsoul.invalid";

/**
 * 身份图地址只收本站地址（2026-10-03，I 批）：以单个 / 开头、解析后仍在本站，原样返回；
 * http(s):// 外站、// 开头、不以 / 开头的相对地址、带空白或反斜杠的都返回 null——这一级跳过，不把身份图链到外站。
 */
function sameOriginPath(raw: string | null | undefined): string | null {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//") || UNSAFE_URL_CHARS.test(raw)) return null;
  try {
    return new URL(raw, PROBE_ORIGIN).origin === PROBE_ORIGIN ? raw : null;
  } catch {
    return null;
  }
}

/**
 * 组出退回链：有照片先是照片；演示模式再垫一张演示小灰猫（和照片同一张就不重复）；最后一级是物种插画（portraitFallback 认得出时）或爪印。
 * 插画、爪印是最后一级，不再往下（插画是站内素材；和居民页 ResidentPortrait 一样，插画之后不再退）。
 * identityUrls（2026-10-03，切换栏用，I 批）：后端给的身份图按先后排在照片前面（切换栏传 [identity.url, identity.fallback_url]）；
 * 只收本站地址（sameOriginPath），外站的那一级跳过；和前面同一张的不重复（重复的一级打不开时会停在破图上）。
 * 不传就是原来的链，一级不差。
 */
export function avatarChain(photoUrl: string | null | undefined, origin: PetOrigin | null | undefined, species: string | null | undefined, identityUrls?: readonly (string | null | undefined)[]): AvatarStep[] {
  const steps: AvatarStep[] = [];
  const has = (src: string) => steps.some((step) => (step.kind === "photo" || step.kind === "demo") && step.src === src);
  for (const raw of identityUrls ?? []) {
    const src = sameOriginPath(raw);
    if (src && !has(src)) steps.push({ kind: "photo", src });
  }
  if (photoUrl && !has(photoUrl)) steps.push({ kind: "photo", src: photoUrl });
  const demo = petPortraitUrl(null);
  if (demo && !has(demo)) steps.push({ kind: "demo", src: demo });
  const art = portraitFallback(origin, species);
  steps.push(art ? { kind: "species", species: art } : { kind: "paw" });
  return steps;
}

export function avatarStepKey(step: AvatarStep): string {
  return step.kind === "photo" || step.kind === "demo" ? `${step.kind}:${step.src}` : step.kind === "species" ? `species:${step.species}` : "paw";
}

/**
 * 按退回链走：图打不开（onError）就往下一级，只往下、不往回，不会在两张图之间来回闪，也不留破图（写法同 ResidentPortrait）。
 * 链变了（换了照片、换了一只）从第一级重新来；晚到的、别的地址的出错不会让它跳级。
 * photoFailed：链里有 TA 的照片、而现在已经退到它后面——这时读屏不说“暂无照片”（TA 其实有照片，只是打不开）。
 */
export function useFallbackChain<T>(chain: readonly T[], keyOf: (step: T) => string): { step: T; at: number; next: () => void } {
  const chainKey = chain.map(keyOf).join("\n");
  const [failed, setFailed] = useState<{ chainKey: string; at: number }>({ chainKey, at: 0 });
  const at = Math.min(failed.chainKey === chainKey ? failed.at : 0, chain.length - 1);
  const next = () =>
    setFailed((prev) => {
      const now = prev.chainKey === chainKey ? prev.at : 0;
      return now === at ? { chainKey, at: at + 1 } : prev;
    });
  return { step: chain[at], at, next };
}

export function useAvatarStep(chain: readonly AvatarStep[]): { step: AvatarStep; photoFailed: boolean; next: () => void } {
  const { step, at, next } = useFallbackChain(chain, avatarStepKey);
  return { step, photoFailed: at > 0 && chain[0]?.kind === "photo", next };
}

/**
 * 没有照片时的中性爪印徽章（ASSET-UI-027 v1，I 10-09 审过：24px 下清楚；出处和指纹见 public/ui-assets/ASSET-UI-027/v1/SOURCE.md）。
 * 它说的是“这里还没有照片”，不代表 TA 的样子：不写名字首字，不借演示猫。两档：96×96 给 48px 以下的 2 倍屏，192×192 给更大的；按头像尺寸挑（sizes）。
 */
export const PAW_BADGE_SRC = "/ui-assets/ASSET-UI-027/v1/neutral-paw@96x96.webp";
export const PAW_BADGE_SRCSET = "/ui-assets/ASSET-UI-027/v1/neutral-paw@96x96.webp 96w, /ui-assets/ASSET-UI-027/v1/neutral-paw@192x192.webp 192w";

/**
 * 全站唯一的“没有照片”爪印（UI-BASE-01b，I 10-09 补：爪印徽章全站统一）。共享头像 PetAvatar、PetPortrait、居民页头像、领证纪念卡、
 * 入住页和旅途页门廊上的 TA、接待页头像、证件照位原来各画各的线条爪印，都换成它；原来的线条爪印组件删掉了。
 * 装饰图（alt=""）：“暂无照片”“照片暂时打不开”写在外面那一层的读屏说法上。size：画出来的边长（px），按它挑两档里的哪一张；
 * className：各处的框子给它定位、定大小（圆头像按比例放大 1.1 倍填满圆，方框、竖框按比例放在正中）。
 */
export function PawBadge({ size, className }: { size: number; className?: string }) {
  const [failed, setFailed] = useState(false);
  const cls = className ? `ps-avatar__paw ${className}` : "ps-avatar__paw";
  // 徽章图本身打不开（UI-BASE-01b，W-23，c84a 10-09：原来没有 onError，就是一张破图，链上也没有下一级可退）：退成纯 CSS 的暖米色圆底（ui.css .ps-avatar__paw.is-fallback），
  // 装饰、不进读屏；“暂无照片”“照片暂时打不开”本来就写在外面那一层，照旧。
  if (failed) return <span className={`${cls} is-fallback`} aria-hidden="true" />;
  return <img className={cls} src={PAW_BADGE_SRC} srcSet={PAW_BADGE_SRCSET} sizes={`${size}px`} alt="" loading="lazy" draggable={false} onError={() => setFailed(true)} />;
}

/**
 * 头像放在什么表面上。"paper"：纸卡（Card paper）、到家卡片这类昼夜都是暖纸的表面——底块、描边、爪印改用纸面那套固定色
 * （--paper 系，见 ui.css 的 .is-on-paper），不跟深色主题走；不给就是跟主题走的样子。PetPortrait 用同一个写法。
 */
export type AvatarSurface = "paper";

/**
 * 当前宠物头像：有照片只用实际照片；没有照片时按 petPortraitUrl 的规则（演示小灰猫 / 爪印），绝不写名字首字，
 * 也不拿通用品种图当成 TA——唯一的例外同 PetPortrait：调用方给了来历 origin、且是领养来的原创居民（portraitFallback），
 * 用居民页同一张物种插画，读屏说明是插画、不是 TA 本人的照片。不给 origin 就是原来的样子（切换栏、家庭页给了）。
 * surface="paper"：放在纸面上（家庭页“住在这里”给了）。
 * species 可以不给（2026-09-27 D4）：数据里没有物种的地方（朋友圈作者 ActorRef 不带物种）不编一个物种，插画这条退回链就不走、落到爪印。
 * 照片打不开（2026-09-27 D4 第二批）：按 avatarChain 往下退——演示小灰猫（只在演示模式）→ 物种插画（只给领养来的原创居民）→ 爪印；
 * 读屏跟着换，退到替身时说“照片暂时打不开”，不说“暂无照片”。图不出错时和原来一模一样。
 * 爪印（UI-BASE-01a）：画 ASSET-UI-027 的中性爪印徽章（PAW_BADGE_SRC），换掉原来的线条爪印和叶绿浅底；读屏照旧——本来就没有照片说“暂无照片”，
 * 有照片但打不开说“照片暂时打不开”，两句分开（nameOnly 时都只说名字）。徽章是装饰（alt=""），说法在外面这一层的 aria-label 上。
 * identityUrls、nameOnly（2026-10-03，切换栏用，I 批）：identityUrls 见 avatarChain；nameOnly 时照片、替身、爪印的读屏都只说名字，
 * 不说“暂无照片”“照片暂时打不开”（物种插画那句说明不是本人照片的照旧）。两样都不给就是原来的样子。
 * 照片缩略档（UI-PET-PHOTO-SRCSET-01，10-13）：/meta 有 pets.photo_sizes 时，后端那三种照片地址按头像边长带一档 w（≤48 取 96、≤96 取 192、更大取 384）；
 * 退回链仍按原地址认级（img 的 key 用原地址）。没有这项能力时和原来一模一样（./photoSizes）。
 */
export function PetAvatar({ name, photoUrl, size = 40, species, origin, surface, identityUrls, nameOnly }: { petId: string; name: string; species?: PetSpecies | null; photoUrl?: string | null; size?: number; origin?: PetOrigin | null; surface?: AvatarSurface; identityUrls?: readonly (string | null | undefined)[]; nameOnly?: boolean }) {
  const { step, photoFailed, next } = useAvatarStep(avatarChain(photoUrl, origin, species, identityUrls));
  const sized = usePhotoSizes();
  // 插画那一步的图打不开（UI-BASE-01b，W-24：原来没有 onError，就是破图）：退到下一步爪印徽章（徽章自己再打不开还有 W-23 的圆底），读屏按爪印那一步说
  const [artFailed, setArtFailed] = useState<string | null>(null);
  const paper = surface === "paper" ? " is-on-paper" : "";
  if (step.kind === "species" && artFailed !== step.species) {
    return (
      <span className={`ps-avatar is-species${paper}`} style={{ width: size, height: size }} role="img" aria-label={speciesArtLabel(step.species, name)} data-species={step.species}>
        <img className="ps-species-art" src={speciesArtUrl(step.species)} alt="" loading="lazy" draggable={false} onError={() => setArtFailed(step.species)} />
      </span>
    );
  }
  const noPhoto = nameOnly ? name : photoFailed ? `${name}，照片暂时打不开` : `${name}，暂无照片`;
  if (step.kind === "paw" || step.kind === "species") {
    return (
      <span className={`ps-avatar is-placeholder${paper}`} style={{ width: size, height: size }} role="img" aria-label={noPhoto}>
        <PawBadge size={size} />
      </span>
    );
  }
  return (
    <span className={`ps-avatar${paper}`} style={{ width: size, height: size }} role="img" aria-label={step.kind === "photo" ? name : noPhoto}>
      <img key={step.src} src={sized ? sizedPhotoSrc(step.src, size) : step.src} alt="" loading="lazy" draggable={false} onError={next} />
    </span>
  );
}
